/**
 * InsurancePage (Phase-2, Module 3)
 *
 * Officer views/links insurance policies. Upload of insurance card / policy
 * document uses the JSON create endpoint (URLs optional) — files can be added
 * via the Medical Document repository if needed.
 *
 * @author Civilhealth Development Team
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, FormControl, InputLabel, Select, IconButton, Tooltip, Chip,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Shield as ShieldIcon, Add as AddIcon, Verified as VerifyIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { PageScaffold, StatusBadge, OfficerSelector } from '../../components/common';
import { InsuranceApi, toAbsoluteFileUrl } from '../../services/phase2Api';
import type { OfficerInsurance, InsuranceCompany } from '../../types/phase2';
import { useCurrentOfficer } from '../../hooks/useCurrentOfficer';
import { useNavigate, useLocation } from 'react-router-dom';

const InsurancePage: React.FC = () => {
  const { officerId } = useCurrentOfficer();
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState<OfficerInsurance[]>([]);
  const [companies, setCompanies] = useState<InsuranceCompany[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    insuranceCompanyId: '' as number | '',
    policyNumber: '',
    policyHolderName: '',
    policyStartDate: '',
    policyEndDate: '',
    coverageAmount: '',
    notes: '',
  });

  const load = useCallback(async () => {
    if (!officerId) return;
    setLoading(true); setError(null);
    try {
      const [data, comps] = await Promise.all([InsuranceApi.listByOfficer(officerId), InsuranceApi.listCompanies()]);
      setRows(data || []); setCompanies(comps || []);
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to load insurance'); }
    finally { setLoading(false); }
  }, [officerId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!officerId || !form.insuranceCompanyId || !form.policyNumber || !form.policyStartDate || !form.policyEndDate || !form.coverageAmount) return;
    setSaving(true); setError(null);
    try {
      await InsuranceApi.create({
        officerId,
        insuranceCompanyId: Number(form.insuranceCompanyId),
        policyNumber: form.policyNumber,
        policyHolderName: form.policyHolderName || undefined,
        policyStartDate: form.policyStartDate,
        policyEndDate: form.policyEndDate,
        coverageAmount: form.coverageAmount ? Number(form.coverageAmount) : undefined,
        notes: form.notes || undefined,
      });
      setOpen(false);
      setForm({ insuranceCompanyId: '', policyNumber: '', policyHolderName: '', policyStartDate: '', policyEndDate: '', coverageAmount: '', notes: '' });
      load();
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to save insurance'); }
    finally { setSaving(false); }
  };

  const columns: GridColDef[] = [
    { field: 'companyName', headerName: 'Company', flex: 1.3, minWidth: 160, renderCell: (p) => p.row.companyName || p.row.insuranceCompanyName || '—' },
    { field: 'policyNumber', headerName: 'Policy No.', width: 150 },
    { field: 'policyHolderName', headerName: 'Holder', width: 150 },
    { field: 'policyStartDate', headerName: 'Start', width: 120 },
    { field: 'policyEndDate', headerName: 'Expiry', width: 120 },
    { field: 'coverageAmount', headerName: 'Coverage', width: 120, renderCell: (p) => p.value ? `₹${Number(p.value).toLocaleString()}` : '—' },
    { field: 'status', headerName: 'Status', width: 140, renderCell: (p) => <StatusBadge status={p.value} size="small" /> },
    {
      field: 'actions', headerName: 'Docs', width: 90, sortable: false,
      renderCell: (p) => {
        const url = toAbsoluteFileUrl(p.row.policyDocumentUrl || p.row.insuranceCardUrl);
        return url ? (
          <Tooltip title="View document"><IconButton size="small" href={url} target="_blank" rel="noopener noreferrer"><ViewIcon fontSize="small" /></IconButton></Tooltip>
        ) : <Chip size="small" label="—" variant="outlined" />;
      },
    },
  ];

  return (
    <PageScaffold title="Insurance" subtitle="Your linked insurance policies" icon={<ShieldIcon />}
      actionLabel={officerId ? 'Add Policy' : undefined} actionIcon={<AddIcon />} onAction={officerId ? () => setOpen(true) : undefined}
      loading={loading && rows.length === 0} error={error} onDismissError={() => setError(null)}>
      {!officerId && (
        <OfficerSelector onSelect={(id) => navigate(`${location.pathname}?officerId=${id}`)} />
      )}
      {officerId && (
      <>
      <Box sx={{ height: 520, bgcolor: '#fff', borderRadius: 3, border: '1px solid #E5E7EB' }}>
        <DataGrid rows={rows} columns={columns} getRowId={(r) => r.id}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} disableRowSelectionOnClick />
      </Box>
      </>
      )}

      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Add Insurance Policy</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Insurance Company</InputLabel>
              <Select label="Insurance Company" value={form.insuranceCompanyId}
                onChange={(e) => setForm({ ...form, insuranceCompanyId: Number(e.target.value) })}>
                {companies.map((c) => <MenuItem key={c.id} value={c.id}>{c.companyName}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Policy Number" required value={form.policyNumber}
              onChange={(e) => setForm({ ...form, policyNumber: e.target.value })} fullWidth />
            <TextField label="Policy Holder Name" value={form.policyHolderName}
              onChange={(e) => setForm({ ...form, policyHolderName: e.target.value })} fullWidth />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField type="date" label="Start Date" InputLabelProps={{ shrink: true }} required value={form.policyStartDate}
                onChange={(e) => setForm({ ...form, policyStartDate: e.target.value })} fullWidth />
              <TextField type="date" label="Expiry Date" InputLabelProps={{ shrink: true }} required value={form.policyEndDate}
                onChange={(e) => setForm({ ...form, policyEndDate: e.target.value })} fullWidth />
            </Box>
            <TextField label="Coverage Amount (₹)" type="number" required value={form.coverageAmount}
              onChange={(e) => setForm({ ...form, coverageAmount: e.target.value })} fullWidth />
            <TextField label="Notes" multiline minRows={2} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}
            disabled={!officerId || !form.insuranceCompanyId || !form.policyNumber || !form.policyStartDate || !form.policyEndDate || !form.coverageAmount || saving}
            sx={{ textTransform: 'none', fontWeight: 600 }}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </PageScaffold>
  );
};

export default InsurancePage;